import { type TAccommodation } from '@/features/filter/accommodation';
import type { TActivityLevel } from '@/features/filter/activity-level';
import { type TComfort } from '@/features/filter/comfort';

export enum EExperienceFilterType {
  SEO_DEFAULT = 'seo_default',
  PERIOD = 'period',
  TOUR_ACTIVITY_SLUGS = 'tour_activity_slugs',
  DURATION = 'duration',
  PRICE = 'price',
  PERSONS = 'persons',
  COMFORT_TYPE = 'comfort_type',
  ACCOMMODATION = 'accommodation',
  ACTIVITY_LEVEL = 'activity_level',
  CHILDREN_ALLOWED = 'children_allowed',
}

export interface IFilterPeriodParams {
  [key: string]: string | undefined | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface IFilterTourActivitySlugsParams {
  [key: string]: string[];
  tour_activity_slugs: string[];
}

export interface IFilterDurationParams {
  [key: string]: [number, number];
  duration: [number, number];
}

export interface ISeoDefaultParams {
  [key: string]: string | number | undefined;
  city: number;
  type: string;
  end_duration?: number;
  start_duration?: number;
  tag__slugs?: string;
}

export interface IFilterPriceParams {
  [key: string]: [number, number] | [null, null];
  price: [number, number] | [null, null];
}

export interface IFilterPersonParams {
  [key: string]: number;
  persons: number;
}

export interface IFilterComfortTypeParams {
  [key: string]: TComfort;
  comfort_type: TComfort;
}

export interface IFilterAccommodationParams {
  [key: string]: TAccommodation;
  accommodation: TAccommodation;
}

export interface IFilterActivityLevelParams {
  [key: string]: TActivityLevel;
  activity_level: TActivityLevel;
}

export interface IFilterChildrenAllowedParams {
  [key: string]: boolean;
  children_allowed: boolean;
}

export interface IExperienceFilter {
  [key: string]: Record<
    string,
    | string
    | string[]
    | number
    | undefined
    | null
    | [number, number]
    | [null, null]
    | TComfort
    | TAccommodation
    | boolean
  >;
  [EExperienceFilterType.PERIOD]: IFilterPeriodParams;
  [EExperienceFilterType.TOUR_ACTIVITY_SLUGS]: IFilterTourActivitySlugsParams;
  [EExperienceFilterType.DURATION]: IFilterDurationParams;
  [EExperienceFilterType.SEO_DEFAULT]: ISeoDefaultParams;
  [EExperienceFilterType.PRICE]: IFilterPriceParams;
  [EExperienceFilterType.PERSONS]: IFilterPersonParams;
  [EExperienceFilterType.COMFORT_TYPE]: IFilterComfortTypeParams;
  [EExperienceFilterType.ACCOMMODATION]: IFilterAccommodationParams;
  [EExperienceFilterType.ACTIVITY_LEVEL]: IFilterActivityLevelParams;
  [EExperienceFilterType.CHILDREN_ALLOWED]: IFilterChildrenAllowedParams;
}

export interface IExperienceQueryParams {
  city: number;
  type: string;
  end_duration?: number;
  start_duration?: number;
  tag__slugs?: string;
  start_date?: string;
  end_date?: string;
  date_filter_mode?: string;
  tour_activity_slugs?: string;
  start_price?: number;
  end_price?: number;
  persons_count?: number;
  comfort_type?: string;
  accommodation_type?: string;
  activity_level_category?: string;
  children_allowed?: boolean;

  offset?: number;
  limit?: number;
  sorting: 'price' | 'rating' | 'rank' | 'published_at';
}

export const DEFAULT_EXPERIENCE_FILTERS: IExperienceFilter = {
  [EExperienceFilterType.PERIOD]: {
    start_date: null,
    end_date: null,
  },
  [EExperienceFilterType.TOUR_ACTIVITY_SLUGS]: {
    tour_activity_slugs: [],
  },
  [EExperienceFilterType.DURATION]: {
    duration: [0, 0],
  },
  [EExperienceFilterType.SEO_DEFAULT]: {
    city: 0,
    type: '',
    end_duration: 0,
    start_duration: 0,
    tag__slugs: '',
  },
  [EExperienceFilterType.PRICE]: {
    price: [null, null],
  },
  [EExperienceFilterType.PERSONS]: {
    persons: 1,
  },
  [EExperienceFilterType.COMFORT_TYPE]: {
    comfort_type: [],
  },
  [EExperienceFilterType.ACCOMMODATION]: {
    accommodation: [],
  },
  [EExperienceFilterType.ACTIVITY_LEVEL]: {
    activity_level: [],
  },
  [EExperienceFilterType.CHILDREN_ALLOWED]: {
    children_allowed: false,
  },
};
